import {
  formatAmount,
  isCancelledTransactionStatus,
  WORKER_SCHEMAS,
  type TableFilters,
  type Transaction,
  type TransactionPageResponse,
  type TransactionSummary,
} from "@aera/data-table";
import { createMarketApiFetch } from "@/lib/marketApiFetch";

const marketApiFetch = createMarketApiFetch();

export const EMPTY_TRANSACTION_SUMMARY: TransactionSummary = {
  count: 0,
  totalAmount: 0,
  currency: "THB",
};

export const DEFAULT_TRANSACTION_WORKER_ID = "bill-lin-income";
export const EXPENSE_TRANSACTION_WORKER_ID = "bill-lin-expense";

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function pickTransactionSummary(
  payload: unknown,
): TransactionSummary | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;

  const nestedTotalFrom = (value: unknown): number | null => {
    if (!value || typeof value !== "object") return null;
    const rec = value as Record<string, unknown>;
    return coerceNumber(rec.total);
  };

  const count =
    coerceNumber(obj.count) ??
    coerceNumber(obj.totalCount) ??
    coerceNumber(obj.total_count) ??
    coerceNumber(obj.transactionCount) ??
    coerceNumber(obj.transaction_count);

  const totalAmount =
    coerceNumber(obj.totalAmount) ??
    coerceNumber(obj.total_amount) ??
    nestedTotalFrom(obj.amount) ??
    coerceNumber(obj.amount) ??
    coerceNumber(obj.sum) ??
    coerceNumber(obj.total);

  if (count == null && totalAmount == null) return null;
  const safeCount = count ?? 0;
  return {
    count: safeCount,
    totalAmount: safeCount === 0 ? 0 : (totalAmount ?? 0),
    currency: "THB",
  };
}

export function formatSignedTransactionAmount(
  summary: TransactionSummary,
  isExpense = false,
): string {
  return `${isExpense ? "−" : "+"}${formatAmount(Math.abs(summary.totalAmount))} ${summary.currency}`;
}

export function formatTransactionSummarySubtitle(
  summary: TransactionSummary,
  isExpense = false,
): string {
  const signedAmount = formatSignedTransactionAmount(summary, isExpense);
  return `${summary.count.toLocaleString("th-TH")} รายการ · ${signedAmount}`;
}

export type CombinedTransactionSummary = {
  income: TransactionSummary;
  expense: TransactionSummary;
  totalCount: number;
};

export async function fetchCombinedWorkspaceTransactionSummary(
  workspaceId: string,
): Promise<CombinedTransactionSummary> {
  const [income, expense] = await Promise.all([
    fetchWorkspaceTransactionSummary(workspaceId, DEFAULT_TRANSACTION_WORKER_ID),
    fetchWorkspaceTransactionSummary(workspaceId, EXPENSE_TRANSACTION_WORKER_ID),
  ]);

  return {
    income,
    expense,
    totalCount: income.count + expense.count,
  };
}

export function formatCombinedTransactionNetAmountLabel(
  summary: CombinedTransactionSummary,
): { label: string; isNegative: boolean } {
  const netAmount = summary.income.totalAmount - summary.expense.totalAmount;
  const currency =
    summary.income.currency || summary.expense.currency || "THB";
  const isNegative = netAmount < 0;

  return {
    isNegative,
    label: isNegative
      ? `−${formatAmount(Math.abs(netAmount))} ${currency}`
      : `+${formatAmount(Math.abs(netAmount))} ${currency}`,
  };
}

export function formatCombinedTransactionSummarySubtitle(
  summary: CombinedTransactionSummary,
): string {
  if (summary.totalCount <= 0) {
    return "ยังไม่มีรายการ";
  }

  const countLabel = `${summary.totalCount.toLocaleString("th-TH")} รายการ`;
  const { label } = formatCombinedTransactionNetAmountLabel(summary);
  return `${countLabel} · ${label}`;
}

export function getBangkokTodayDateKey(date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function getTransactionBangkokDateKey(
  record: Transaction,
): string | null {
  const extended = record as Transaction & {
    record_date?: unknown;
    updated_at?: unknown;
    created_at?: unknown;
  };
  // Prefer created_at (table "วันที่" / เวลาสร้าง), then record_date / updated_at.
  const raw = unwrapDisplayValue(
    extended.created_at ??
      extended.record_date ??
      record.recorded_at ??
      extended.updated_at,
  );
  if (raw == null) return null;

  const text = String(raw).trim();
  if (!text) return null;

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function isTransactionOnBangkokDate(
  record: Transaction,
  dateKey: string,
): boolean {
  return getTransactionBangkokDateKey(record) === dateKey;
}

function unwrapDisplayValue(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value !== "object") return value;
  if ("value" in value) {
    return unwrapDisplayValue((value as { value: unknown }).value);
  }
  return value;
}

function getTransactionRecordedAtMs(record: Transaction): number | null {
  const extended = record as Transaction & {
    record_date?: unknown;
    created_at?: unknown;
  };
  const raw = unwrapDisplayValue(
    extended.record_date ?? record.recorded_at ?? extended.created_at,
  );
  if (raw == null) return null;

  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (raw instanceof Date) {
    const ms = raw.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const text = String(raw).trim();
  if (!text) return null;

  const asNumber = Number(text);
  if (Number.isFinite(asNumber)) return asNumber;

  const ms = Date.parse(text);
  return Number.isFinite(ms) ? ms : null;
}

function compareTransactionsByRecordedAtDesc(
  left: Transaction,
  right: Transaction,
): number {
  const leftMs = getTransactionRecordedAtMs(left);
  const rightMs = getTransactionRecordedAtMs(right);
  if (leftMs == null && rightMs == null) return 0;
  if (leftMs == null) return 1;
  if (rightMs == null) return -1;
  return rightMs - leftMs;
}

function coerceTransactionAmount(record: Transaction): number | null {
  const income = record as Transaction & {
    transfer_amount?: unknown;
    total_amount?: unknown;
    amount?: unknown;
  };
  const candidates = [
    income.transfer_amount,
    income.total_amount,
    income.amount,
  ];

  for (const candidate of candidates) {
    const value = unwrapDisplayValue(candidate);
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
}

function buildTransactionListUrl(
  workspaceId: string,
  workerId: string,
  filters: Partial<TableFilters>,
): string {
  const schema =
    workerId in WORKER_SCHEMAS
      ? WORKER_SCHEMAS[workerId as keyof typeof WORKER_SCHEMAS]
      : WORKER_SCHEMAS[DEFAULT_TRANSACTION_WORKER_ID];

  const baseUrl = new URL(schema.apiEndpoint, "http://localhost");
  const apiParams = new URLSearchParams(baseUrl.searchParams.toString());

  apiParams.set("worker", schema.workerId);
  apiParams.set("workspace_id", workspaceId.trim());

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) return;
    if (key === "worker" || key === "workspaceId" || key === "workspace_id") {
      return;
    }
    if (key === "sort" && (value === "recorded_at" || value === "record_date")) {
      apiParams.set("sort", "created_at");
    } else {
      apiParams.set(key, value);
    }
  });

  if (
    (schema.workerId === "bill-lin-income" ||
      schema.workerId === "bill-lin-expense") &&
    !apiParams.get("date_field") &&
    (apiParams.get("from") || apiParams.get("to"))
  ) {
    apiParams.set("date_field", "created_at");
  }

  apiParams.delete("cursor");
  return `/api/v1/transactions?${apiParams.toString()}`;
}

export async function fetchWorkspaceTransactions(
  workspaceId: string,
  workerId: string = DEFAULT_TRANSACTION_WORKER_ID,
  filters: Partial<TableFilters> = {},
): Promise<Transaction[]> {
  const ws = workspaceId.trim();
  if (!ws) return [];

  const res = await marketApiFetch(
    buildTransactionListUrl(ws, workerId, filters),
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Fetch failed: ${res.status}${text ? ` — ${text.slice(0, 180)}` : ""}`,
    );
  }

  const body = (await res.json()) as TransactionPageResponse;
  return Array.isArray(body.data) ? body.data : [];
}

export type TodayWorkspaceTransactions = {
  count: number;
  latestTransaction: Transaction | null;
  latestIsExpense: boolean | null;
};

export type RecentWorkspaceTransaction = {
  record: Transaction;
  isExpense: boolean;
};

export const RECENT_WORKSPACE_TRANSACTION_LIMIT = 15;

async function fetchMergedWorkspaceTransactions(
  workspaceId: string,
): Promise<RecentWorkspaceTransaction[]> {
  const listFilters: Partial<TableFilters> = {
    sort: "updated_at",
    dir: "desc",
  };

  const [incomeRecords, expenseRecords] = await Promise.all([
    fetchWorkspaceTransactions(
      workspaceId,
      DEFAULT_TRANSACTION_WORKER_ID,
      listFilters,
    ),
    fetchWorkspaceTransactions(
      workspaceId,
      EXPENSE_TRANSACTION_WORKER_ID,
      listFilters,
    ),
  ]);

  return [
    ...incomeRecords.map((record) => ({ record, isExpense: false })),
    ...expenseRecords.map((record) => ({ record, isExpense: true })),
  ].sort((left, right) =>
    compareTransactionsByRecordedAtDesc(left.record, right.record),
  );
}

export async function fetchRecentWorkspaceTransactions(
  workspaceId: string,
  limit = RECENT_WORKSPACE_TRANSACTION_LIMIT,
): Promise<RecentWorkspaceTransaction[]> {
  const ws = workspaceId.trim();
  if (!ws) return [];

  const merged = await fetchMergedWorkspaceTransactions(ws);
  return merged.slice(0, limit);
}

export async function fetchTodayWorkspaceTransactions(
  workspaceId: string,
): Promise<TodayWorkspaceTransactions> {
  const today = getBangkokTodayDateKey();
  const merged = await fetchMergedWorkspaceTransactions(workspaceId);
  const todayRecords = merged.filter(({ record }) =>
    isTransactionOnBangkokDate(record, today),
  );

  const latest = todayRecords[0] ?? null;

  return {
    count: todayRecords.length,
    latestTransaction: latest?.record ?? null,
    latestIsExpense: latest?.isExpense ?? null,
  };
}

export function formatLatestTransactionDirectionLabel(
  isExpense: boolean,
): string {
  return isExpense ? "มีเงินออก" : "มีเงินเข้า";
}

export function formatLatestTransactionAmountLabel(
  latestTransaction: Transaction | null,
  isExpense: boolean,
): string {
  if (!latestTransaction) return "—";

  const latestAmount = coerceTransactionAmount(latestTransaction);
  if (latestAmount == null) return "—";

  return `${isExpense ? "−" : "+"}${formatAmount(Math.abs(latestAmount))} THB`;
}

export function formatTodayIncomeDetailRow(input: {
  todayCount: number;
  latestTransaction: Transaction | null;
  latestIsExpense?: boolean | null;
}): string {
  const { todayCount, latestTransaction, latestIsExpense = false } = input;

  if (todayCount <= 0) {
    return "วันนี้ยังไม่มีรายการเข้าออก";
  }

  const amountLabel = formatLatestTransactionAmountLabel(
    latestTransaction,
    latestIsExpense === true,
  );

  return `${formatLatestTransactionDirectionLabel(latestIsExpense === true)} (${amountLabel})`;
}

function applyTableDateFilter(
  records: Transaction[],
  filters: Partial<TableFilters>,
): Transaction[] {
  const from = filters.from?.trim() || null;
  const to = filters.to?.trim() || null;
  if (!from && !to) return records;

  return records.filter((record) => {
    const key = getTransactionBangkokDateKey(record);
    if (!key) return false;
    if (from && key < from) return false;
    if (to && key > to) return false;
    return true;
  });
}

function shouldExcludeCancelledFromSummary(
  filters: Partial<TableFilters>,
): boolean {
  const status = filters.status?.trim() ?? "";
  if (!status) return true;
  return !/(ยกเลิก|cancel)/i.test(status);
}

function summarizeTransactions(
  records: Transaction[],
  filters: Partial<TableFilters> = {},
): TransactionSummary {
  const rows = shouldExcludeCancelledFromSummary(filters)
    ? records.filter((record) => !isCancelledTransactionStatus(record.status))
    : records;

  let totalAmount = 0;
  for (const record of rows) {
    const amount = coerceTransactionAmount(record);
    if (amount != null) totalAmount += amount;
  }

  const count = rows.length;
  return {
    count,
    // Never show a non-zero total with zero rows (stale/partial payload guard).
    totalAmount: count === 0 ? 0 : totalAmount,
    currency: "THB",
  };
}

async function fetchAllWorkspaceTransactions(
  workspaceId: string,
  workerId: string,
  filters: Partial<TableFilters>,
): Promise<Transaction[]> {
  const records: Transaction[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  let pages = 0;
  let stagnantPages = 0;

  while (pages < 100) {
    pages += 1;
    const path = buildTransactionListUrl(workspaceId, workerId, filters);
    const url = new URL(path, "http://localhost");
    if (cursor) url.searchParams.set("cursor", cursor);

    const res = await marketApiFetch(`${url.pathname}${url.search}`);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Fetch failed: ${res.status}${text ? ` — ${text.slice(0, 180)}` : ""}`,
      );
    }

    const body = (await res.json()) as TransactionPageResponse;
    const beforeCount = records.length;
    if (Array.isArray(body.data)) {
      for (const raw of body.data) {
        const record = raw as Transaction;
        const id = String(
          unwrapDisplayValue((record as { record_id?: unknown }).record_id) ??
            "",
        ).trim();
        if (id) {
          if (seen.has(id)) continue;
          seen.add(id);
        }
        records.push(record);
      }
    }

    if (records.length === beforeCount) stagnantPages += 1;
    else stagnantPages = 0;
    // Upstream kept returning pages that add no new unique rows — stop.
    if (stagnantPages >= 2) break;

    const nextCursor = body.nextCursor?.trim() || null;
    if (!nextCursor) break;
    if (nextCursor === cursor) break;
    cursor = nextCursor;
    if (records.length > 20_000) break;
  }

  return records;
}

async function fetchWorkspaceTransactionSummaryFromList(
  workspaceId: string,
  workerId: string,
  filters: Partial<TableFilters>,
): Promise<TransactionSummary> {
  const records = await fetchAllWorkspaceTransactions(
    workspaceId,
    workerId,
    filters,
  );
  return summarizeTransactions(applyTableDateFilter(records, filters), filters);
}

function parseSummaryResponse(body: unknown): TransactionSummary {
  const candidate =
    body && typeof body === "object" && ("data" in body || "summary" in body)
      ? ((body as Record<string, unknown>).data ??
        (body as Record<string, unknown>).summary)
      : body;

  return pickTransactionSummary(candidate) ?? EMPTY_TRANSACTION_SUMMARY;
}

export async function fetchWorkspaceTransactionSummary(
  workspaceId: string,
  workerId: string = DEFAULT_TRANSACTION_WORKER_ID,
  filters: Partial<TableFilters> = {},
): Promise<TransactionSummary> {
  const ws = workspaceId.trim();
  if (!ws) return EMPTY_TRANSACTION_SUMMARY;

  const schema =
    workerId in WORKER_SCHEMAS
      ? WORKER_SCHEMAS[workerId as keyof typeof WORKER_SCHEMAS]
      : WORKER_SCHEMAS[DEFAULT_TRANSACTION_WORKER_ID];

  const baseUrl = new URL(schema.apiEndpoint, "http://localhost");
  const apiParams = new URLSearchParams(baseUrl.searchParams.toString());

  apiParams.set("worker", schema.workerId);
  apiParams.set("workspace_id", ws);

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) return;
    if (
      key === "worker" ||
      key === "workspaceId" ||
      key === "workspace_id" ||
      key === "sort" ||
      key === "dir"
    ) {
      return;
    }
    apiParams.set(key, value);
  });

  apiParams.delete("cursor");
  apiParams.delete("limit");
  apiParams.delete("sort");
  apiParams.delete("dir");

  const hasDateFilter = Boolean(filters.from?.trim() || filters.to?.trim());
  const hasStatusFilter = Boolean(filters.status?.trim());
  // Summary should exclude cancelled rows unless the user is explicitly
  // filtering to cancelled status. The upstream summary endpoint counts all
  // rows, so compute from the transaction list when that rule applies.
  // Hard timeout so the UI never sticks on "กำลังโหลด…".
  const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`Summary timed out after ${ms}ms`)),
            ms,
          );
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  if (hasDateFilter || !hasStatusFilter) {
    return withTimeout(
      fetchWorkspaceTransactionSummaryFromList(ws, workerId, filters),
      15_000,
    );
  }

  const res = await marketApiFetch(
    `/api/v1/transactions/summary?${apiParams.toString()}`,
  );
  if (res.ok) {
    return parseSummaryResponse((await res.json()) as unknown);
  }

  if (res.status >= 500) {
    return withTimeout(
      fetchWorkspaceTransactionSummaryFromList(ws, workerId, filters),
      15_000,
    );
  }

  const text = await res.text().catch(() => "");
  throw new Error(
    `Fetch failed: ${res.status}${text ? ` — ${text.slice(0, 180)}` : ""}`,
  );
}
